
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { PolicyWithProfile } from "@/components/admin/contact/policy/types/policy";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";

const AdminPoliciesPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [policies, setPolicies] = useState<PolicyWithProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminAndFetchPolicies = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          navigate("/auth");
          return;
        }

        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .single();

        if (!roles || roles.role !== "admin") {
          toast({
            variant: "destructive",
            title: "Access Denied",
            description: "You don't have permission to access this page.",
          });
          navigate("/dashboard");
          return;
        }

        const { data, error } = await supabase
          .from("policies")
          .select(`
            *,
            producer:profiles!policies_producer_id_fkey (
              first_name,
              last_name
            )
          `);

        if (error) {
          console.error("Error fetching policies:", error);
          toast({
            variant: "destructive",
            title: "Error fetching policies",
            description: error.message,
          });
          return;
        }

        setPolicies(data as PolicyWithProfile[]);
      } catch (error) {
        console.error("Error in checkAdminAndFetchPolicies:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "An unexpected error occurred",
        });
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndFetchPolicies();
  }, [navigate, toast]);

  if (loading) {
    return (
      <PageLayout>
        <div className="container mx-auto py-8">
          <p>Loading...</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <SidebarProvider>
        <div className="flex min-h-screen w-full flex-col md:flex-row">
          <DashboardSidebar />
          <div className="flex-1">
            <div className="p-4 md:p-8 overflow-x-auto">
              <h1 className="text-2xl font-bold mb-6">Policies</h1>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[120px]">Policy Number</TableHead>
                      <TableHead className="min-w-[120px]">Customer</TableHead>
                      <TableHead className="min-w-[120px]">Producer</TableHead>
                      <TableHead className="min-w-[100px]">Type</TableHead>
                      <TableHead className="min-w-[100px]">Status</TableHead>
                      <TableHead className="min-w-[100px]">Premium</TableHead>
                      <TableHead className="min-w-[120px]">Start Date</TableHead>
                      <TableHead className="min-w-[120px]">End Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {policies.map((policy) => (
                      <TableRow key={policy.id}>
                        <TableCell className="font-medium">{policy.policy_number}</TableCell>
                        <TableCell>
                          {policy.customer_first_name} {policy.customer_last_name}
                        </TableCell>
                        <TableCell>{policy.producer_name || 'Not Assigned'}</TableCell>
                        <TableCell>{policy.insurance_type}</TableCell>
                        <TableCell>{policy.status}</TableCell>
                        <TableCell>${policy.premium_amount}</TableCell>
                        <TableCell>{new Date(policy.start_date).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(policy.end_date).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </SidebarProvider>
    </PageLayout>
  );
};

export default AdminPoliciesPage;
